import { type VariantProps } from "class-variance-authority";
import { ArrowLeft, Construction, FileQuestion, Home, LifeBuoy, ShieldOff, TriangleAlert } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "./button";
import {
  unavailablePageIconVariants,
  unavailablePageTitleVariants,
} from "./unavailable-page-variants";

type UnavailablePageVariant = NonNullable<
  VariantProps<typeof unavailablePageIconVariants>["variant"]
>;

interface UnavailablePageProps {
  variant?: UnavailablePageVariant;
  supportHref?: string;
  resetError?: () => void;
}

const VARIANT_CONTENT = {
  unfinished: {
    icon: Construction,
    titleKey: "unavailablePage.unfinished.title",
    titleDefault: "This page isn't available right now",
    descriptionKey: "unavailablePage.unfinished.description",
    descriptionDefault: "We're still building this one. Check back soon.",
  },
  integrationDisabled: {
    icon: ShieldOff,
    titleKey: "unavailablePage.integrationDisabled.title",
    titleDefault: "This integration isn't enabled",
    descriptionKey: "unavailablePage.integrationDisabled.description",
    descriptionDefault: "Contact support to turn it on.",
  },
  error: {
    icon: TriangleAlert,
    titleKey: "errors.somethingWentWrong",
    titleDefault: "Something went wrong",
    descriptionKey: "unavailablePage.error.description",
    descriptionDefault: "An unexpected error occurred. Please try again.",
  },
  notFound: {
    icon: FileQuestion,
    titleKey: "errors.pageNotFound",
    titleDefault: "Page not found",
    descriptionKey: "unavailablePage.notFound.description",
    descriptionDefault: "The page you're looking for doesn't exist.",
  },
} as const satisfies Record<UnavailablePageVariant, unknown>;

const DEFAULT_SUPPORT_HREF = "mailto:team@onlineresults.nl";

function UnavailablePage({
  variant = "unfinished",
  supportHref = DEFAULT_SUPPORT_HREF,
  resetError,
}: UnavailablePageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const { icon: Icon, titleKey, titleDefault, descriptionKey, descriptionDefault } =
    VARIANT_CONTENT[variant];

  const hasHistory = location.key !== "default";

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleBack = () => {
    resetError?.();
    if (hasHistory) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const backLabel = hasHistory
    ? t("unavailablePage.goBack", { defaultValue: "Go back" })
    : t("unavailablePage.takeMeHome", { defaultValue: "Take me home" });

  const showSupport = variant === "integrationDisabled";

  return (
    <section
      aria-labelledby="unavailable-page-title"
      className="flex flex-col items-center justify-center gap-4 py-16 text-muted-foreground"
    >
      <div className={unavailablePageIconVariants({ variant })}>
        <Icon aria-hidden="true" className="size-6 stroke-[1.5]" />
      </div>

      <h1
        id="unavailable-page-title"
        ref={headingRef}
        tabIndex={-1}
        className={unavailablePageTitleVariants({ variant })}
      >
        {t(titleKey, { defaultValue: titleDefault })}
      </h1>
      <p className="text-sm text-center max-w-md">
        {t(descriptionKey, { defaultValue: descriptionDefault })}
      </p>

      <div className="flex items-center gap-2 mt-2">
        {showSupport && (
          <Button variant="ghost" asChild>
            <a href={supportHref}>
              <LifeBuoy aria-hidden="true" className="size-4" />
              {t("unavailablePage.contactSupport", { defaultValue: "Contact support" })}
            </a>
          </Button>
        )}
        <Button variant="outline" onClick={handleBack}>
          {hasHistory ? (
            <ArrowLeft aria-hidden="true" className="size-4" />
          ) : (
            <Home aria-hidden="true" className="size-4" />
          )}
          {backLabel}
        </Button>
      </div>
    </section>
  );
}

export default UnavailablePage;
export type { UnavailablePageVariant };
